import { useEffect, useRef, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import type { PermissionRef, RoleSummary } from '../../api/types.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { QuietButton } from '../../components/QuietButton.tsx';
import { actionError } from '../../components/actionError.ts';
import { groupPermissionsByResource, resourceLabel } from './rolesHelpers.ts';

export interface RolePermissionsEditorProps {
  open: boolean;
  role: RoleSummary | null;
  onClose: () => void;
  onSaved: () => void;
}

export function RolePermissionsEditor({
  open,
  role,
  onClose,
  onSaved,
}: Readonly<RolePermissionsEditorProps>): React.JSX.Element | null {
  const { api } = useAuth();

  const [catalog, setCatalog] = useState<PermissionRef[] | null>(null);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!open || !role) return;
    setChecked(new Set(role.permissions.map((p) => p.id)));
    setCatalog(null);
    setLoadError('');
    setError('');
    setBusy(false);
    submittingRef.current = false;

    let cancelled = false;
    api.roles.listPermissions().then(
      (res) => {
        if (!cancelled) setCatalog(res.permissions);
      },
      (e: unknown) => {
        if (!cancelled) setLoadError(actionError(e));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [open, role, api]);

  if (!role) return null;

  const toggle = (id: number, on: boolean) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const submit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setBusy(true);
    setError('');
    try {
      await api.roles.setPermissions(role.id, Array.from(checked));
      onSaved();
      onClose();
    } catch (e) {
      setError(actionError(e));
    } finally {
      setBusy(false);
      submittingRef.current = false;
    }
  };

  const groups = groupPermissionsByResource(catalog ?? []);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Permissions -- {role.name}</DialogTitle>
      <DialogContent>
        {catalog === null && !loadError && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
            <CircularProgress size={20} />
            <Typography>Loading permissions...</Typography>
          </Box>
        )}
        {loadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        )}
        {catalog !== null && (
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {groups.map((group) => (
              <Box key={group.resource}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {resourceLabel(group.resource)}
                </Typography>
                <Stack>
                  {group.permissions.map((perm) => (
                    <FormControlLabel
                      key={perm.id}
                      control={
                        <Checkbox
                          checked={checked.has(perm.id)}
                          disabled={busy}
                          onChange={(e) => toggle(perm.id, e.target.checked)}
                          slotProps={{ input: { 'aria-label': perm.code } }}
                        />
                      }
                      label={perm.description}
                    />
                  ))}
                </Stack>
              </Box>
            ))}
            <FormHelperText>Changes take effect immediately for logged-in users of this role.</FormHelperText>
            {error && (
              <Alert role="alert" severity="error" sx={{ fontWeight: 700 }}>
                {error}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <QuietButton onClick={onClose} disabled={busy}>
          Cancel
        </QuietButton>
        <Button onClick={() => void submit()} disabled={busy || catalog === null}>
          Save permissions
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RolePermissionsEditor;
